import Image from "next/image"
import { useEffect, useState } from "react"
import styled from "@emotion/styled"

const CHAT_URL =
  "https://ai-web-dev-a8eqcwajeufmh0ee.westeurope-01.azurewebsites.net/chat"
const token = process.env.NEXT_PUBLIC_MINTAI_CHAT_TOKEN || ""

// Chat Icon Image Wrapper
const IconWrapper = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

// Chat Icon Image Component
const ChatIcon = () => (
  <IconWrapper>
    <Image src="/chat-icon.png" alt="Chat" width={30} height={30} priority />
  </IconWrapper>
)

const FloatingButtonContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9998;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;

  @media (max-width: 768px) {
    bottom: 15px;
    right: 15px;
  }
`

const FloatingButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #001064 0%, #1d88fa 100%);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 16, 100, 0.4), 0 8px 24px rgba(0, 16, 100, 0.2);
  transition: all 0.3s ease;
  font-size: 0;
  padding: 0;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(0, 16, 100, 0.6),
      0 10px 30px rgba(0, 16, 100, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 24px;
    height: 24px;
    color: white;
  }

  @media (max-width: 768px) {
    width: 56px;
    height: 56px;

    svg {
      width: 22px;
      height: 22px;
    }
  }
`

interface FloatingChatButtonSimpleProps {
  onChatOpen?: () => void
}

declare global {
  interface Window {
    MintAIChatWidget?: {
      create: (
        config: { chatId: string },
        url: string
      ) => {
        open: (token: string) => void
        close: () => void
      }
    }
    mintAiWidget?: {
      open: (token: string) => void
      close: () => void
    }
  }
}

/**
 * Floating Chat Button using MintAIChatWidget SDK
 * This component loads the chat widget SDK and opens the chat on click
 */
export default function FloatingChatButtonSimple({
  onChatOpen,
}: FloatingChatButtonSimpleProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [isSDKReady, setIsSDKReady] = useState(false)

  useEffect(() => {
    const initializeSDK = () => {
      // Check if widget is already created
      if (window.mintAiWidget) {
        setIsSDKReady(true)
        setIsLoading(false)
        return
      }

      // Check if MintAIChatWidget SDK is available
      if (window.MintAIChatWidget && window.MintAIChatWidget.create) {
        try {
          window.mintAiWidget = window.MintAIChatWidget.create(
            { chatId: "web" },
            CHAT_URL
          )
          setIsSDKReady(true)
          setIsLoading(false)
        } catch (error) {
          console.error("Failed to create chat widget:", error)
          setIsLoading(false)
        }
      } else {
        // Load the SDK script
        const script = document.createElement("script")
        script.src = CHAT_URL.replace("/chat", "/sdk/chat-widget.js")
        script.onload = () => {
          if (window.MintAIChatWidget && window.MintAIChatWidget.create) {
            try {
              window.mintAiWidget = window.MintAIChatWidget.create(
                { chatId: "web" },
                CHAT_URL
              )
              setIsSDKReady(true)
            } catch (error) {
              console.error("Failed to create chat widget:", error)
            }
          }
          setIsLoading(false)
        }
        script.onerror = () => {
          console.error("Failed to load chat widget SDK")
          setIsLoading(false)
        }
        document.head.appendChild(script)
      }
    }

    initializeSDK()
  }, [])

  const handleOpenChat = () => {
    if (window.mintAiWidget && typeof window.mintAiWidget.open === "function") {
      window.mintAiWidget.open(token)
      onChatOpen?.()
    } else {
      console.error("Chat widget is not ready")
    }
  }

  return (
    <FloatingButtonContainer>
      <FloatingButton
        onClick={handleOpenChat}
        aria-label="Open chat"
        title="Need help?"
        disabled={isLoading || !isSDKReady}
        style={{
          opacity: isLoading || !isSDKReady ? 0.6 : 1,
          cursor: isLoading || !isSDKReady ? "not-allowed" : "pointer",
        }}
      >
        <ChatIcon />
        {/* <TooltipText>Need help?</TooltipText> */}
      </FloatingButton>
    </FloatingButtonContainer>
  )
}
